import React, { useState, useEffect } from "react";
import type { ConfigProviderProps } from "antd";
import { Button, Tabs } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import { MedicineBoxOutlined, PlusOutlined } from "@ant-design/icons";
import Colors from "../../constants/Colors";
import Eprescriptionlist from "../../components/card/eprescription-list";
import Labrequestlist from "../../components/card/labrequest-list";
import Xraylist from "../../components/card/x-raylist";
import { E_PRESCRIPTIONS_BASE } from "../../constants/e-prescriptions";

type SizeType = ConfigProviderProps["componentSize"];

export default function EPrescription() {
  const size: SizeType = "large";
  const location = useLocation();
  const navigate = useNavigate();
  const defaultActiveKey =
    new URLSearchParams(location.search).get("tab") || "1";
  const [activeKey, setActiveKey] = useState(defaultActiveKey);

  useEffect(() => {
    setActiveKey(new URLSearchParams(location.search).get("tab") || "1");
  }, [location]);

  const handleTabChange = (key: string) => {
    navigate(`${E_PRESCRIPTIONS_BASE}?tab=${key}`);
    setActiveKey(key);
  };

  const TAB_ACTIONS: Record<string, { label: string; path: string }> = {
    "1": { label: "Create E-prescription", path: `${E_PRESCRIPTIONS_BASE}/new` },
    "2": { label: "New lab request", path: `${E_PRESCRIPTIONS_BASE}/labrequest` },
    "3": { label: "New X-Ray request", path: `${E_PRESCRIPTIONS_BASE}/xray` },
  };
  const activeAction = TAB_ACTIONS[activeKey] ?? TAB_ACTIONS["1"];

  return (
    <div className="min-h-[70vh] bg-[#f8fafc]">
      {/* Page header — matches the plain, bold-title header language the
          rest of the app (Appointments, Profile) already uses, rather than
          a separate heavy-gradient hero. One action button whose label and
          destination follow whichever tab is active, replacing what used to
          be two separate "Create E-prescription" buttons on screen at once
          (one here, one duplicated inside the Prescriptions tab) — and a
          hero CTA that was hardcoded to prescription-creation even while
          looking at the Lab or X-Ray tab. */}
      <div className="flex flex-wrap items-start justify-between gap-4 px-4 pt-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-medpharma-slate tracking-tight m-0 flex items-center gap-2">
            <MedicineBoxOutlined className="text-primary" aria-hidden />
            E-Prescriptions
          </h1>
          <p className="mt-1 mb-0 text-sm text-slate-500">
            Issue prescriptions and diagnostic requests, and track their status until they reach
            the patient.
          </p>
        </div>
        <Button
          type="primary"
          size="large"
          icon={<PlusOutlined />}
          onClick={() => navigate(activeAction.path)}
          style={{ background: Colors.primary }}
          className="shrink-0 font-semibold"
        >
          {activeAction.label}
        </Button>
      </div>

      <div className="mx-4 mt-4 mb-6">
        <Tabs
          activeKey={activeKey}
          onChange={handleTabChange}
          size={size}
          className="bg-white rounded-xl shadow-card px-4 pt-2 pb-4"
          items={[
            {
              label: "Prescriptions",
              key: "1",
              children: (
                <div className="min-h-[50vh] pt-4">
                  <Eprescriptionlist />
                </div>
              ),
            },
            {
              label: "Lab requests",
              key: "2",
              children: (
                <div className="min-h-[50vh] pt-4">
                  <Labrequestlist />
                </div>
              ),
            },
            {
              label: "X-Ray requests",
              key: "3",
              children: (
                <div className="min-h-[50vh] pt-4">
                  <Xraylist />
                </div>
              ),
            },
          ]}
        />
      </div>
    </div>
  );
}
